'use client';

import { useForm } from '@tanstack/react-form';
import { ArrowRight, CheckCircle2, MessageSquare } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { apiClient } from '../../../lib/api/client';
import { contactInterestOptions } from './contact-interest-options';
import { LandingFooter } from './landing-footer';
import { LandingNavbar } from './landing-navbar';
import { marketingFacts } from './marketing-facts';

const contactFormSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
  phone: z.string(),
  email: z.string().trim().email('Enter a valid email'),
  businessName: z.string(),
  interest: z.string(),
  message: z.string(),
});

const benefits = [
  {
    title: 'Inventory Infrastructure',
    description: 'End-to-end inventory management, from sourcing to fulfillment.',
  },
  {
    title: 'Omnichannel Distribution',
    description: 'List and sync inventory across 50+ sales channels.',
  },
  {
    title: 'Fulfillment Services',
    description: 'We handle storage, QC, and shipping so you can focus on sales.',
  },
];

export function ContactMarketingPage() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      businessName: '',
      interest: '',
      message: '',
    },
    onSubmit: async ({ value }) => {
      const contact = contactFormSchema.safeParse(value);
      if (!contact.success) {
        toast.error(contact.error.issues[0]?.message ?? 'Check the form and try again.');
        return;
      }

      const about = [
        contact.data.businessName && `Business: ${contact.data.businessName}`,
        contact.data.phone && `Phone: ${contact.data.phone}`,
        contact.data.interest && `Interest: ${contact.data.interest}`,
      ]
        .filter((detail): detail is string => Boolean(detail))
        .join('\n');

      try {
        const response = await apiClient.contact.$post({
          json: {
            name: `${contact.data.firstName} ${contact.data.lastName}`,
            email: contact.data.email,
            about,
            message:
              contact.data.message || 'Contact request submitted from the wholesale website.',
          },
        });
        if (!response.ok) {
          throw new Error('Contact request failed');
        }
        form.reset();
        setIsSubmitted(true);
      } catch {
        toast.error('Failed to send message. Please try again.');
      }
    },
  });

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background">
        <LandingNavbar />
        <main className="mx-auto flex max-w-xl flex-col items-center justify-center px-4 py-24 text-center sm:px-6">
          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" aria-hidden="true" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Message received
          </h1>
          <p className="mt-4 text-muted-foreground">
            Thanks for reaching out. Our team will get back to you within 24 hours.
          </p>
          <Link
            href="/"
            className="mt-8 inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Back to Home
            <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </Link>
        </main>
        <LandingFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <LandingNavbar />
      <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <div className="mb-8">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/5 px-4 py-1.5 text-sm text-brand">
                <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
                Get in touch
              </div>
              <h1 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
                Talk to us
              </h1>
              <p className="mt-4 text-muted-foreground">
                Tell us about your business and what you&apos;re looking for. We&apos;ll get back to
                you within 24 hours.
              </p>
            </div>

            <form
              className="space-y-6"
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                form.handleSubmit();
              }}
            >
              <div className="grid grid-cols-2 gap-4">
                <form.Field name="firstName">
                  {(field) => (
                    <ContactInput
                      id="firstName"
                      label="First Name"
                      value={field.state.value}
                      onChange={field.handleChange}
                      placeholder="John"
                      autoComplete="given-name"
                      required
                    />
                  )}
                </form.Field>
                <form.Field name="lastName">
                  {(field) => (
                    <ContactInput
                      id="lastName"
                      label="Last Name"
                      value={field.state.value}
                      onChange={field.handleChange}
                      placeholder="Smith"
                      autoComplete="family-name"
                      required
                    />
                  )}
                </form.Field>
              </div>
              <form.Field name="phone">
                {(field) => (
                  <ContactInput
                    id="phone"
                    label="Phone Number"
                    type="tel"
                    value={field.state.value}
                    onChange={field.handleChange}
                    placeholder="+1 (555) 123-4567"
                    autoComplete="tel"
                  />
                )}
              </form.Field>
              <form.Field name="email">
                {(field) => (
                  <ContactInput
                    id="email"
                    label="Email"
                    type="email"
                    value={field.state.value}
                    onChange={field.handleChange}
                    placeholder="john@company.com"
                    autoComplete="email"
                    required
                  />
                )}
              </form.Field>
              <form.Field name="businessName">
                {(field) => (
                  <ContactInput
                    id="businessName"
                    label="Business Name"
                    value={field.state.value}
                    onChange={field.handleChange}
                    placeholder="Acme Co."
                    autoComplete="organization"
                  />
                )}
              </form.Field>
              <form.Field name="interest">
                {(field) => (
                  <label htmlFor="interest" className="block space-y-2">
                    <span className="text-sm font-medium">What are you interested in?</span>
                    <select
                      id="interest"
                      value={field.state.value}
                      onChange={(event) => field.handleChange(event.target.value)}
                      className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {contactInterestOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </form.Field>
              <form.Field name="message">
                {(field) => (
                  <label htmlFor="message" className="block space-y-2">
                    <span className="text-sm font-medium">Message</span>
                    <textarea
                      id="message"
                      value={field.state.value}
                      onChange={(event) => field.handleChange(event.target.value)}
                      placeholder="Tell us more about your business and what you're looking for..."
                      rows={4}
                      className="flex w-full resize-y rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </label>
                )}
              </form.Field>
              <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                {([canSubmit, isSubmitting]) => (
                  <button
                    type="submit"
                    disabled={!canSubmit || isSubmitting}
                    className="inline-flex h-11 w-full items-center justify-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? 'Sending…' : 'Send Message'}
                    {!isSubmitting && <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />}
                  </button>
                )}
              </form.Subscribe>
              <p className="text-center text-sm text-muted-foreground">
                By submitting, you agree to our Terms of Service and Privacy Policy.
              </p>
            </form>
          </div>

          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-2xl border border-border bg-card p-6 shadow-xl">
              <h2 className="mb-4 font-semibold text-foreground">Why work with OS Group?</h2>
              <div className="space-y-4">
                {benefits.map((benefit) => (
                  <div
                    key={benefit.title}
                    className="rounded-lg border border-border/60 bg-muted/30 p-4"
                  >
                    <h3 className="font-medium text-foreground">{benefit.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{benefit.description}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4 border-t border-border pt-6 text-center">
                {[
                  [marketingFacts.platformVolume, 'Platform Volume'],
                  [marketingFacts.activeMerchants, 'Active Merchants'],
                  [marketingFacts.cataloguedSkus, 'Catalogued SKUs'],
                  [marketingFacts.yearsInBusiness, 'Years in Business'],
                ].map(([value, label]) => (
                  <div key={label}>
                    <p className="text-2xl font-semibold text-foreground">{value}</p>
                    <p className="text-xs text-muted-foreground">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </main>
      <LandingFooter />
    </div>
  );
}

function ContactInput({
  id,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
  required = false,
}: {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  autoComplete: string;
  required?: boolean;
}) {
  return (
    <label htmlFor={id} className="block space-y-2">
      <span className="text-sm font-medium">{label}</span>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    </label>
  );
}
